import { useMemo } from 'react';
import { m } from 'framer-motion';
import { varHover } from '@emfood/shared';
import { useNavigate } from '@tanstack/react-router';
import Iconify from '@emfood/shared/src/components/iconify';

import { useTheme } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import { Box, Badge, Tooltip, Typography } from '@mui/material';

import { paths } from 'src/routes/paths';

import { useGetCart } from 'src/api/hooks/api-hooks';

// ----------------------------------------------------------------------

const CartButton: React.FC = () => {
  const navigate = useNavigate();
  const { data: cart } = useGetCart();
  const theme = useTheme();

  const totalItems: number = useMemo(
    () => cart?.baskets.reduce((sum, basket) => sum + basket.items.length, 0) || 0,
    [cart]
  );

  const totalValue: number = useMemo(
    () =>
      cart?.baskets.reduce(
        (sum, basket) =>
          sum +
          basket.items.reduce(
            (basketSum, item) => basketSum + item.activePrice * parseInt(item.quantity, 10),
            0
          ),
        0
      ) || 0,
    [cart]
  );

  const handleClick = () => {
    navigate({ to: paths.dashboard.checkout.root, search: {} });
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Tooltip
        enterDelay={300}
        title="Click here to see basket contents and continue to checkout process"
      >
        <IconButton
          component={m.button}
          whileTap="tap"
          whileHover="hover"
          variants={varHover(1.05)}
          onClick={handleClick}
          aria-label={`Cart with ${totalItems} items worth £${totalValue.toFixed(2)}. This button will take you to the checkout page.`}
          sx={{
            width: 45,
            height: 45,
          }}
        >
          <Badge
            badgeContent={totalItems}
            max={99}
            sx={{
              '& .MuiBadge-badge': {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                // darker red for contrast
                backgroundColor: '#AD2000',
                color: theme.palette.common.white,
              },
            }}
          >
            <Iconify
              icon="solar:cart-3-bold"
              width={24}
              sx={{
                color: theme.palette.grey[800],
                '&:hover': {
                  color: theme.palette.grey[900],
                },
                transition: 'color 0.3s ease',
              }}
            />
          </Badge>
        </IconButton>
      </Tooltip>
      <Typography
        variant="body2"
        sx={{
          ml: 1,
          color: theme.palette.text.primary,
        }}
      >
        £{totalValue.toFixed(2)}
      </Typography>
    </Box>
  );
};

export default CartButton;
